import { faLockKeyhole } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import InputText from '@sharedComponents/InputText';
import Select from '@sharedComponents/Select';
import Typography from '@sharedComponents/Typography';
import { formatDate } from '@utils/date';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CLIENT_CATEGORIES, getClientTypeOptions, getCsmOptions } from '../../helpers';

const FieldLabel = ({ label, locked = false }) => (
    <FlexBox gap="xxs" alignItems="center">
        <Typography color="neutral-primary" fontWeight="regular">
            {label}
        </Typography>
        {locked && <FontAwesomeIcon icon={faLockKeyhole} style={{ color: 'var(--text-color-neutral-tertiary)', fontSize: '12px' }} />}
    </FlexBox>
);

const InformationsTab = ({ client, csmUsers = [], onSave = () => {} } = {}) => {
    const { t } = useTranslation('bo');

    const [name, setName] = useState(client?.name ?? '');
    const [reference, setReference] = useState(client?.reference ?? '');
    const [category, setCategory] = useState(client?.category ?? 'NONE');
    const [active, setActive] = useState(client?.active ?? true);
    const [csmUserId, setCsmUserId] = useState(client?.csm?.uuid ?? '');
    const [clientType, setClientType] = useState(client?.clientType ?? '');

    const categoryOptions = [{ value: 'NONE', label: t('No category') }, ...CLIENT_CATEGORIES.map((value) => ({ value, label: value }))];
    const statusOptions = [
        { value: 'ACTIVE', label: t('Active') },
        { value: 'INACTIVE', label: t('Inactive') },
    ];
    const csmOptions = getCsmOptions(t, csmUsers);
    const clientTypeOptions = getClientTypeOptions(t);

    const isDirty =
        name !== client?.name ||
        reference !== client?.reference ||
        category !== (client?.category ?? 'NONE') ||
        active !== client?.active ||
        csmUserId !== (client?.csm?.uuid ?? '') ||
        clientType !== (client?.clientType ?? '');

    const handleSave = () =>
        onSave({
            name: name.trim(),
            reference: reference.trim(),
            category: category === 'NONE' ? null : category,
            active,
            csmUserId: csmUserId || null,
            clientType: clientType || null,
        });

    return (
        <FlexBox flexDirection="column" gap="l">
            <FlexBox gap="xl" flexWrap="wrap" alignItems="flex-start">
                <FlexBox flexDirection="column" gap="m" additionnalStyles={{ flex: '1 1 260px' }}>
                    <FlexBox flexDirection="column" gap="xxs">
                        <FieldLabel label={t('Name')} />
                        <InputText value={name} onChange={(e) => setName(e.target.value)} />
                    </FlexBox>

                    <FlexBox flexDirection="column" gap="xxs">
                        <FieldLabel label={t('Reference')} />
                        <InputText value={reference} onChange={(e) => setReference(e.target.value)} />
                    </FlexBox>

                    <FlexBox flexDirection="column" gap="xxs">
                        <FieldLabel label={t('Category')} />
                        <Select
                            options={categoryOptions}
                            value={category}
                            onChange={(opt) => setCategory(opt.value)}
                            isSearchable={false}
                        />
                    </FlexBox>

                    <FlexBox flexDirection="column" gap="xxs">
                        <FieldLabel label={t('Status')} />
                        <Select
                            options={statusOptions}
                            value={active ? 'ACTIVE' : 'INACTIVE'}
                            onChange={(opt) => setActive(opt.value === 'ACTIVE')}
                            isSearchable={false}
                        />
                    </FlexBox>
                </FlexBox>

                <FlexBox flexDirection="column" gap="m" additionnalStyles={{ flex: '1 1 260px' }}>
                    <FlexBox flexDirection="column" gap="xxs">
                        <FieldLabel label={t('CSM')} />
                        <Select options={csmOptions} value={csmUserId} onChange={(opt) => setCsmUserId(opt.value)} isSearchable />
                    </FlexBox>

                    <FlexBox flexDirection="column" gap="xxs">
                        <FieldLabel label={t('Client type')} />
                        <Select
                            options={clientTypeOptions}
                            value={clientType}
                            onChange={(opt) => setClientType(opt.value)}
                            isSearchable={false}
                        />
                    </FlexBox>

                    <FlexBox flexDirection="column" gap="xxs">
                        <FieldLabel label={t('Created at')} locked />
                        <InputText value={client?.createdAt ? formatDate(client.createdAt) : ''} disabled readOnly />
                    </FlexBox>
                </FlexBox>
            </FlexBox>

            <FlexBox>
                <Button label={t('Save modifications')} disabled={!isDirty} action={handleSave} />
            </FlexBox>
        </FlexBox>
    );
};

export default InformationsTab;
